/**
 * 130 — repairs: a phone (or anything) a customer left to be fixed.
 *
 * The walk is received → in_progress → ready → returned, and any step but the
 * last may end in cancelled. Money is NOT kept here: a repair is charged at
 * the till as an ordinary sale (a no-product line plus any parts), so the
 * drawer, the Z-report and the reports see it the usual way — this row only
 * remembers which sale it was (`pickup_sale_id`). A warranty repair is handed
 * back free, with no sale.
 *
 * The customer's texts (the WhatsApp „ready" note, the intake ticket) are
 * Georgian whatever the screen speaks — they are written to the customer, like
 * the receipt and the debt reminder.
 */

import { esc, m2, printThermal, thermalHeader, type PrintableShop } from './print'
import { waPhone, whatsAppLink } from './debt-reminder'

export type RepairStatus = 'received' | 'in_progress' | 'ready' | 'returned' | 'cancelled'

export const REPAIR_STATUSES: RepairStatus[] = ['received', 'in_progress', 'ready', 'returned', 'cancelled']

export type Repair = {
  id: string
  branch_id: string | null
  number: number
  status: RepairStatus
  customer_id: string | null
  customer_name: string
  customer_phone: string | null
  device: string
  serial: string | null
  problem: string
  accessories: string | null
  condition: string | null
  technician: string | null
  estimate: number | null
  warranty: boolean
  sale_ref: string | null
  final_price: number | null
  note: string | null
  pickup_sale_id: string | null
  created_at: string
  updated_at: string
  ready_at: string | null
  returned_at: string | null
}

/** What a new intake sends; the database adds the number, the clock and the org. */
export type RepairDraft = Pick<Repair,
  'customer_name' | 'customer_phone' | 'customer_id' | 'device' | 'serial' | 'problem' | 'accessories' |
  'condition' | 'technician' | 'estimate' | 'warranty' | 'sale_ref' | 'branch_id'>

const num = (v: unknown) => (v == null ? null : Number(v))

export function mapRepair(r: any): Repair {
  return {
    id: r.id,
    branch_id: r.branch_id ?? null,
    number: Number(r.number ?? 0),
    status: REPAIR_STATUSES.includes(r.status) ? r.status : 'received',
    customer_id: r.customer_id ?? null,
    customer_name: r.customer_name ?? '',
    customer_phone: r.customer_phone ?? null,
    device: r.device ?? '',
    serial: r.serial ?? null,
    problem: r.problem ?? '',
    accessories: r.accessories ?? null,
    condition: r.condition ?? null,
    technician: r.technician ?? null,
    estimate: num(r.estimate),
    warranty: r.warranty === true,
    sale_ref: r.sale_ref ?? null,
    final_price: num(r.final_price),
    note: r.note ?? null,
    pickup_sale_id: r.pickup_sale_id ?? null,
    created_at: r.created_at,
    updated_at: r.updated_at,
    ready_at: r.ready_at ?? null,
    returned_at: r.returned_at ?? null,
  }
}

/** R-0007 — the shop's own number, on the ticket and in every search. */
export const repairNo = (n: number) => `R-${String(n).padStart(4, '0')}`

/** The one step forward from here; null at the end of the walk. */
export function nextStatus(s: RepairStatus): RepairStatus | null {
  if (s === 'received') return 'in_progress'
  if (s === 'in_progress') return 'ready'
  if (s === 'ready') return 'returned'
  return null
}

/** Still in the shop's hands — what the list shows first. */
export const isOpen = (s: RepairStatus) => s === 'received' || s === 'in_progress' || s === 'ready'

/** What the customer pays at pickup: the final price, else the quote; a warranty repair is free. */
export function amountDue(r: Pick<Repair, 'warranty' | 'final_price' | 'estimate'>): number {
  if (r.warranty) return 0
  return Math.max(0, r.final_price ?? r.estimate ?? 0)
}

/** Matches a search box: number (7, R-0007), name, phone digits, device or serial. */
export function repairMatches(r: Repair, q: string): boolean {
  const s = q.trim().toLowerCase()
  if (!s) return true
  const digits = s.replace(/\D/g, '')
  if (repairNo(r.number).toLowerCase().includes(s)) return true
  if (digits && String(r.number) === digits.replace(/^0+/, '')) return true
  if (r.customer_name.toLowerCase().includes(s) || r.device.toLowerCase().includes(s)) return true
  if (digits.length >= 3 && (r.customer_phone ?? '').replace(/\D/g, '').includes(digits)) return true
  const bare = s.replace(/\s+/g, '')
  if (bare.length >= 4 && (r.serial ?? '').toLowerCase().includes(bare)) return true
  return false
}

/** The cart line that charges a repair at the till (a no-product line). */
export const REPAIR_LINE_PREFIX = 'repair:'
export const repairLineId = (id: string) => `${REPAIR_LINE_PREFIX}${id}`
export const isRepairLine = (productId: string | null | undefined) => !!productId && productId.startsWith(REPAIR_LINE_PREFIX)
export const repairLineName = (r: Pick<Repair, 'number' | 'device'>) => `შეკეთება ${repairNo(r.number)} · ${r.device}`

/** „Your phone is ready" — the customer's WhatsApp, typed and waiting to be sent. */
export function readyMessage(r: Pick<Repair, 'number' | 'device' | 'customer_name' | 'warranty' | 'final_price' | 'estimate'>, shop: string): string {
  const due = amountDue(r)
  return (
    `გამარჯობა ${r.customer_name}, ${shop}-დან გწერთ: თქვენი ${r.device} (${repairNo(r.number)}) მზადაა` +
    (due > 0 ? `, გადასახდელია ${m2(due)} ლარი` : '') +
    `. შეგიძლიათ წაიღოთ — თან იქონიეთ მიღების ქვითარი. გმადლობთ!`
  )
}

export function readyWhatsApp(r: Repair, shop: string): string | null {
  const phone = waPhone(r.customer_phone)
  return phone ? whatsAppLink(phone, readyMessage(r, shop)) : null
}

/** The intake ticket — 72 mm, Georgian, two signatures. The customer keeps it. */
export function repairTicketHtml(r: Repair, shop: PrintableShop): string {
  const row = (k: string, v: string | null | undefined) =>
    v && v.trim() ? `<div class="row"><span>${esc(k)}</span><span>${esc(v)}</span></div>` : ''
  const d = new Date(r.created_at)
  const two = (n: number) => String(n).padStart(2, '0')
  const when = `${two(d.getDate())}.${two(d.getMonth() + 1)}.${d.getFullYear()} ${two(d.getHours())}:${two(d.getMinutes())}`
  return `
  ${thermalHeader(shop)}
  <hr>
  <div class="c" style="font-weight:700;font-size:14px">შეკეთების ქვითარი ${esc(repairNo(r.number))}</div>
  <div class="c">${esc(when)}</div>
  <hr>
  ${row('კლიენტი', r.customer_name)}
  ${row('ტელეფონი', r.customer_phone)}
  ${row('მოწყობილობა', r.device)}
  ${row('IMEI / S/N', r.serial)}
  ${row('პრობლემა', r.problem)}
  ${row('თან დატოვა', r.accessories)}
  ${row('მდგომარეობა', r.condition)}
  ${r.warranty ? row('გარანტია', 'საგარანტიო შეკეთება') : row('სავარაუდო ფასი', r.estimate != null ? `₾${m2(r.estimate)}` : null)}
  <hr>
  <div style="font-size:10px">მოწყობილობას გაგატანთ ამ ქვითრით. ფასი შეიძლება შეიცვალოს დიაგნოსტიკის შემდეგ — წინასწარ შეგატყობინებთ.</div>
  <div class="row" style="margin-top:6mm"><span>მიმღები: ________</span><span>კლიენტი: ________</span></div>`
}

export function printRepairTicket(r: Repair, shop: PrintableShop): boolean {
  return printThermal(`შეკეთება ${repairNo(r.number)}`, repairTicketHtml(r, shop))
}
